import { useMe } from "@@/authentication/me/me-context";
import { useAcceptQuoteCommandMutation } from "@@/backoffice/for-providers/quotes/hooks/use-accept-quote-mutation";
import type { ModalId } from "@@/modals/context/modal-id";
import { createMagicModalContext } from "@@/modals/magic-modal.context";
import { Modal } from "@@/modals/modal";
import { ModalHeader } from "@@/modals/modal-header";
import { browserLogger } from "@@/settings";
import { ButtonWhite } from "@@/shared/buttons_v2/button-gray";
import { ButtonPrimary } from "@@/shared/buttons_v2/button-primary";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { Form3 } from "@@/shared/form/form3.form";
import { CheckBoxWithText, TextBox } from "@@/shared/text";
import type { Quote } from "@towni/common";
import { acceptQuoteCommandFactory, translation } from "@towni/common";
import React from "react";
import { z } from "zod";

type PropsModal = {
    modalId: ModalId;
    quote: Quote;
    close: () => void;
};

const formSchema = z.object({
    name: z.string(),
    email: z.string().email(),
});

const AcceptQuoteModal = (props: PropsModal) => {
    const accept = useAcceptQuoteCommandMutation();
    const [me] = useMe();
    const [acceptTerms, setAcceptTerms] = React.useState(false);
    return (
        <Modal
            modalId={props.modalId}
            onHide={props.close}
            header={
                <ModalHeader
                    modalId={props.modalId}
                    bottomBorder
                    css={{
                        flex: 0,
                    }}
                    title={translation({
                        sv: "Acceptera offert",
                    })}
                />
            }>
            <FlexColumn className="space-y-4 p-4">
                <Form3
                    initializeIfNotExists={{
                        clearOnUnmount: true,
                        schema: formSchema,
                        initialData: {
                            name: me ? me.firstName + " " + me.lastName : "",
                            email: me?.email.emailAddress ?? "",
                        },
                    }}>
                    {({ FormComponents, form }) => (
                        <FlexColumn className="space-y-4">
                            <FormComponents.TextInput
                                fieldId="name"
                                label={translation({
                                    sv: "Namn",
                                    en: "Name",
                                })}
                            />
                            <FormComponents.TextInput
                                fieldId="email"
                                type="email"
                                label={translation({
                                    sv: "E-post",
                                    en: "Email",
                                })}
                            />

                            <CheckBoxWithText
                                text={translation({
                                    sv: "Jag godkänner offerten och dess villkor",
                                    en: "I accept the quote and its terms",
                                })}
                                checked={acceptTerms}
                                onChange={() => {
                                    setAcceptTerms(!acceptTerms);
                                }}
                            />

                            <FlexRow className="mt-4 gap-2" fillParentWidth>
                                <ButtonWhite
                                    onClick={() => {
                                        props.close();
                                    }}>
                                    <TextBox
                                        text={translation({
                                            sv: "Avbryt",
                                            en: "Cancel",
                                        })}
                                        size="M"
                                    />
                                </ButtonWhite>
                                <ButtonPrimary
                                    disabled={!acceptTerms}
                                    onClick={async () => {
                                        const data = form.safeParse();
                                        if (data.success && acceptTerms) {
                                            await accept.mutateAsync(
                                                acceptQuoteCommandFactory({
                                                    quoteId: props.quote._id,
                                                    providerId:
                                                        props.quote.providerId,
                                                    acceptedBy: {
                                                        email: data.data.email,
                                                        name: data.data.name,
                                                    },
                                                }),
                                            );
                                            props.close();
                                        } else {
                                            browserLogger.error(
                                                "Form validation failed",
                                                data.error,
                                            );
                                        }
                                    }}>
                                    <TextBox
                                        text={translation({
                                            sv: "Acceptera",
                                            en: "Accept",
                                        })}
                                        size="M"
                                    />
                                </ButtonPrimary>
                            </FlexRow>
                        </FlexColumn>
                    )}
                </Form3>
            </FlexColumn>
        </Modal>
    );
};

const {
    MagicModalContextProvider,
    useMagicModalContext: useMagicAcceptQuoteModalContext,
} = createMagicModalContext<PropsModal, "close" | "quote">(AcceptQuoteModal);

const MagicAcceptQuoteModalContextProvider = (props: {
    readonly children: React.ReactNode;
}) => {
    return (
        <MagicModalContextProvider>{props.children}</MagicModalContextProvider>
    );
};

export {
    AcceptQuoteModal,
    MagicAcceptQuoteModalContextProvider,
    useMagicAcceptQuoteModalContext,
};
